import { useErrorHandler } from "@smartrr/shared/components/ErrorBoundary";
import { OrgUtils } from "@smartrr/shared/entities/Organization";
import { IPurchasableVariant } from "@smartrr/shared/entities/PurchasableVariant";
import { IPurchaseState, PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import { getPurchStFutureScheduledDeliveriesWithNonActive } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import {
  ISmartrrSellingPlan,
  ISmartrrSellingPlanGroup,
  SellingPlanConfigs,
} from "@smartrr/shared/entities/SellingPlanGroup";
import { ensureShopifyGid } from "@smartrr/shared/utils/ensureShopifyGid";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import flatten from 'lodash/flatten';
import { useMemo } from "react";
import { toast } from "react-toastify";
import { create } from "zustand";

import {
  toCartItem,
  validateCartChanges,
} from "@customer-app/account/views/OperationAquarius/components/cartValidation";
import {
  getItemPriceWithSellingPlan,
  getOrderCount,
} from "@customer-app/account/views/OperationAquarius/utils/Pricing/Item";
import {
  addSubscriptionLineItem,
  removeSubscriptionLineItem,
  swapSubscriptionLineItem,
  updateSubscriptionLineItem,
} from "@customer-app/state/actionCreators/customerPurchaseStates";
import { VariantPurchaseOptions } from "@customer-app/state/reducers/sellingPlanGroups";
import { GeneralCustomerDispatch } from "@customer-app/state/typedCustomerRedux";
import { useSmartrrCustomerDispatch } from "@customer-app/state/typedCustomerReduxHooks";

import { useOrganization } from "../../../utils";
import { getPlan } from "../../../utils/Pricing/Delivery";
import { useSortedPurchaseStates } from "../../hooks/useSortedPurchaseStates";
import { CountryCode } from "@smartrr/shared/countryCode";

export const useSubscriptionStoreInit = (cps?: IPurchaseState | undefined) => {
  const sortedPurchaseStates = useSortedPurchaseStates() ?? [];
  const flattenedPurchaseStates = flatten(sortedPurchaseStates.map(([, purchaseStates]) => purchaseStates));

  const purchaseState = useMemo(
    () => (cps ? cps : flattenedPurchaseStates.length ? flattenedPurchaseStates[0] : undefined),
    [sortedPurchaseStates, cps]
  );

  const { dispatch: storedDispatch, setDispatch, setDelivery } = useSubscriptionActions();
  const dispatch = useSmartrrCustomerDispatch();
  const organization = useOrganization();
  const errorHandler = useErrorHandler();

  return useMemo(() => {
    if (purchaseState?.schedule && organization) {
      try {
        const scheduledDeliveries = getPurchStFutureScheduledDeliveriesWithNonActive(
          purchaseState,
          OrgUtils.getBillingSchedule(organization),
          1
        );
        // TODO: Update how delivery is fetched to instead pull from `DeliveryAccess.useUpcomingDelivery`
        const delivery = scheduledDeliveries?.length ? scheduledDeliveries[0] : undefined;

        if (delivery) {
          setDelivery(delivery);
        } else {
          throw new Error("No delivery found");
        }

        if (!storedDispatch) {
          setDispatch(dispatch);
        }

        return delivery;
      } catch (error) {
        errorHandler("Error initializing subscription store: " + error);
      }
    }
  }, [purchaseState, purchaseState?.schedule, organization]);
};

export interface SubscriptionActionStore {
  delivery: PurchaseStateAndDelivery | null;
  states: {
    isLoading: boolean;
    showCancelSubscription: boolean;
  };
  actions: {
    dispatch: GeneralCustomerDispatch | null;
    setDelivery: (delivery: PurchaseStateAndDelivery) => void;
    getDelivery: () => PurchaseStateAndDelivery | null;
    setDispatch: (dispatch: ReturnType<typeof useSmartrrCustomerDispatch>) => void;
    setIsLoading: (state: boolean) => void;
    onRemove: (lineItemId: string) => Promise<void>;
    onAddItem: (
      delivery: PurchaseStateAndDelivery,
      selectedOption: VariantPurchaseOptions,
      quantity: number,
      sellingPlanGroups: ISmartrrSellingPlanGroup[],
      options: {
        addItemAsSubscription?: boolean;
        configType?: SellingPlanConfigs;
        scheduleIndex?: number | undefined;
      },
      countryCode?: CountryCode
    ) => Promise<void>;
    onSwapItem: (
      variant: IPurchasableVariant,
      quantity: number,
      sellingPlanId: string,
      previousItemId: string,
      selectedPlan: ISmartrrSellingPlan | null,
      swapAsAddon: boolean,
      countryCode?: CountryCode
    ) => Promise<void>;
  };
}
// TODO: Add try catches and error handling with useErrorHandler()
export const useSubscriptionActionStore = create<SubscriptionActionStore>()((set, get) => ({
  delivery: null,
  states: {
    isLoading: false,
    showCancelSubscription: false,
  },
  actions: {
    dispatch: null,

    setDispatch: dispatch => set({ actions: { ...get().actions, dispatch } }),

    setDelivery: (delivery: PurchaseStateAndDelivery) => set(state => ({ ...state, delivery })),

    getDelivery: () => get().delivery,

    setIsLoading: x => set(state => ({ ...state, states: { ...state.states, isLoading: x } })),

    async onAddItem(
      delivery,
      selectedOption,
      quantity,
      sellingPlanGroups,
      options = { addItemAsSubscription: false, configType: SellingPlanConfigs.ADDON, scheduleIndex: undefined },
      countryCode
    ) {
      const { actions } = get();
      const { dispatch, setIsLoading } = actions;

      if (!delivery?.purchaseState || !dispatch) {
        return;
      }

      const { purchaseState } = delivery;
      const { sellingPlanId } = purchaseState;

      const skdIdx = options.addItemAsSubscription ? undefined : options.scheduleIndex;

      if (!quantity) {
        toast.error(typedLocalize("subscriptions.invalidQuantityToast"));
        return;
      }

      setIsLoading(true);
      try {
        let spId = sellingPlanId;
        if (selectedOption[options.configType!] && !options.addItemAsSubscription) {
          spId = ensureShopifyGid("SellingPlan", selectedOption[options.configType!]?.shopifyNumericId!);
        }

        if (spId === null) {
          setIsLoading(false);
          return;
        }

        const selectedPlan = getPlan(sellingPlanGroups, spId);

        const isValidUpdate = validateCartChanges(delivery, {
          addedItems: [
            {
              quantity,
              isAddOn: !options.addItemAsSubscription,
              variantShopifyId: selectedOption.vnt.shopifyId,
              sellingPlanShopifyId: sellingPlanId,
              estimatedPrice: getItemPriceWithSellingPlan(
                selectedOption.vnt,
                purchaseState.currency,
                selectedPlan,
                getOrderCount(delivery),
                undefined
              ),
            },
          ],
        });

        if (!isValidUpdate) {
          setIsLoading(false);
          return;
        }

        if (selectedOption[options.configType!] && !options.addItemAsSubscription) {
          spId = ensureShopifyGid("SellingPlan", selectedOption[options.configType!]?.shopifyNumericId!);
        }

        const duplicatedItems = purchaseState.stLineItems.filter(item => {
          const vntIdMatch = item.vnt.id === selectedOption.vnt.id;
          const spIdMatch = options.addItemAsSubscription
            ? item.sellingPlanId === sellingPlanId && !item.isAddOn
            : item.sellingPlanId === spId;
          const isReferralsLineItem = item.isRedeemed;
          const skdIdxMatch = options.addItemAsSubscription ? true : item.skdIdx === skdIdx;

          return !!(vntIdMatch && spIdMatch && skdIdxMatch && !isReferralsLineItem);
        });

        if (duplicatedItems.length) {
          const newQuantity = quantity + duplicatedItems[0].quantity;
          return await dispatch(
            updateSubscriptionLineItem(
              purchaseState.id,
              duplicatedItems[0].id,
              {
                quantity: newQuantity,
                variantId: duplicatedItems[0].vnt.id,
                sellingPlanId,
                countryCode,
              },
              toast
            )
          ).then(() => setIsLoading(false));
        }
        if (countryCode) {
          await dispatch(
            addSubscriptionLineItem(
              purchaseState.id,
              selectedOption.vnt.id,
              quantity,
              spId!,
              toast,
              skdIdx,
              countryCode
            )
          );
          setIsLoading(false);
          return;
        }

        await dispatch(
          addSubscriptionLineItem(purchaseState.id, selectedOption.vnt.id, quantity, spId!, toast, skdIdx)
        );
        setIsLoading(false);
      } catch (error) {
        return error;
      } finally {
        setIsLoading(false);
      }
    },

    async onRemove(lineItemId) {
      const { delivery, actions } = get();
      const { dispatch, setIsLoading } = actions;

      if (!delivery?.purchaseState || !dispatch) {
        return;
      }

      const { purchaseState } = delivery;
      const lineItem = purchaseState.stLineItems.find(li => li.id === lineItemId);
      const isAddon = lineItem ? lineItem.isAddOn : false;

      if (isAddon || purchaseState.stLineItems.length > 1) {
        const isValidUpdate = validateCartChanges(delivery, { removedItemIds: [lineItemId] });
        if (!isValidUpdate) {
          return;
        }
        setIsLoading(true);

        await dispatch(removeSubscriptionLineItem(purchaseState.id, lineItemId))
          .then(res => {
            switch (res.type) {
              case "PURCHASE_STATE_LINE_ITEM_REMOVE_SUCCESS": {
                toast(typedLocalize(`subscriptions.yourSubscriptions.orderOptionsModal.removedItemToast`));
                set(state => ({ ...state, states: { ...state.states, showCancelSubscription: false } }));
                break;
              }
              case "PURCHASE_STATE_LINE_ITEM_REMOVE_ERROR": {
                toast.error(
                  typedLocalize(`subscriptions.yourSubscriptions.orderOptionsModal.removeItemErrorToast`)
                );
                set(state => ({ ...state, states: { ...state.states, showCancelSubscription: false } }));
                break;
              }
              case "PURCHASE_STATE_LINE_ITEM_REMOVE_ERROR_LAST_ITEM": {
                /* Do not show toast here */
                set(state => ({ ...state, states: { ...state.states, showCancelSubscription: true } }));
                break;
              }
              default: {
                return;
              }
            }
          })
          .catch(error => toast(error))
          .finally(() => setIsLoading(false));
      } else {
        toast.error(typedLocalize(`subscriptions.yourSubscriptions.orderOptionsModal.removeItemQtyErrorToast`));
      }
    },

    async onSwapItem(variant, quantity, sellingPlanId, previousItemId, selectedPlan, swapAsAddon, countryCode) {
      const { delivery, actions } = get();
      const { dispatch, setIsLoading } = actions;

      if (!delivery?.purchaseState || !dispatch) {
        return;
      }

      const { purchaseState } = delivery;

      setIsLoading(true);
      try {
        const duplicatedItems = purchaseState.stLineItems.filter(item => {
          const vntIdMatch = item.vnt.id === variant.id;
          const spIdMatch = item.sellingPlanId === sellingPlanId && !item.isAddOn;

          const isReferralsLineItem = item.isRedeemed;

          return !!(vntIdMatch && spIdMatch && !isReferralsLineItem);
        });
        if (duplicatedItems.length) {
          // remove the swapped item and move quantity from it to the duplicate item
          const joinedQuantity = quantity + duplicatedItems[0].quantity;
          const isValid = validateCartChanges(delivery, {
            removedItemIds: [previousItemId],
            updatedItems: [
              {
                ...toCartItem(delivery, duplicatedItems[0]),
                quantity: joinedQuantity,
              },
            ],
          });
          if (!isValid) {
            return;
          }

          await dispatch(removeSubscriptionLineItem(purchaseState.id, previousItemId));
          await dispatch(
            updateSubscriptionLineItem(
              purchaseState.id,
              duplicatedItems[0].id,
              {
                variantId: duplicatedItems[0].vnt.id,
                quantity: joinedQuantity,
                countryCode,
              },
              toast
            )
          );
        } else {
          const isValid = validateCartChanges(delivery, {
            updatedItems: [
              {
                id: previousItemId,
                variantShopifyId: variant.shopifyId,
                isAddOn: swapAsAddon,
                sellingPlanShopifyId: sellingPlanId,
                quantity,
                estimatedPrice: getItemPriceWithSellingPlan(
                  variant,
                  delivery.purchaseState.currency,
                  selectedPlan,
                  getOrderCount(delivery)
                ),
              },
            ],
          });
          if (!isValid) {
            return; // TODO: Add additional error messaging
          }
          // replace an item if it's not already in subscriptions
          await dispatch(
            swapSubscriptionLineItem(
              purchaseState.id,
              previousItemId,
              variant.id,
              quantity,
              sellingPlanId,
              toast,
              undefined,
              countryCode
            )
          ).then(res => {
            if (res.type === "PURCHASE_STATE_LINE_ITEM_SWAP_SUCCESS") {
              toast(typedLocalize("subscriptions.yourSubscriptions.orderOptionsModal.swapItemView.swappedToast"));
            } else if (res.type === "PURCHASE_STATE_LINE_ITEM_SWAP_ERROR") {
              console.error(`${res.payload.errorMessage}`);
            }
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
        // Modal closing is handled in onSwapItem.finally()
      }
    },
  },
}));

export const useSubscriptionActions = () => {
  return useSubscriptionActionStore(state => state.actions);
};

export const useSubscriptionActionStates = () => {
  return useSubscriptionActionStore(state => state.states);
};

export const useSubscriptionDelivery = () => {
  return useSubscriptionActionStore(state => state.delivery);
};

export const useIsLoading = () => {
  return useSubscriptionActionStore(state => state.states.isLoading);
};
